/**
 * What every mark of the hour chart means, as the marks themselves — one
 * row under the chart. The rule colours show only when a rule fired.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { Zap } from 'lucide-react';

import { ExcludedMark } from '../../../rules/RuleHits';
import { RULE_ACTIONS, RULE_TONE_COLOR } from '../../../rules/rule-surfaces';
import { TagDot } from '../../../rules/tags';

const Item: FC<{ mark: ReactNode; children: ReactNode }> = ({ mark, children }) => (
  <span className="inline-flex items-center gap-1.5">
    {mark}
    {children}
  </span>
);

const Bar: FC<{ color: string }> = ({ color }) => <span className="inline-block h-3 w-2 rounded-t-sm" style={{ background: color }} />;

export const HourChartLegend: FC<{ hasRules: boolean; hasContainer?: boolean; hasTags?: boolean }> = ({ hasRules, hasContainer = true, hasTags }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground">
    <Item mark={<span className="flex h-4 w-4 items-center justify-center rounded-full bg-warning font-mono text-[10px] font-bold text-warning-foreground">1</span>}>
      the day&apos;s best hours
    </Item>
    <Item mark={<Bar color="hsl(var(--primary) / 0.8)" />}>{hasContainer ? <>this container&apos;s hours — taller = better</> : <>inside the country&apos;s window — taller = better</>}</Item>
    <Item mark={<Bar color="hsl(var(--muted-foreground) / 0.25)" />}>{hasContainer ? <>hours it doesn&apos;t run in, or outside the window</> : <>outside the window — no drop goes there</>}</Item>
    <Item mark={<ExcludedMark />}>excluded — not judged yet, never bad</Item>
    <Item mark={<span className="inline-block h-2.5 w-0.5 rounded-full bg-foreground/70" />}>a drop</Item>
    <Item mark={<Zap className="h-3 w-3 fill-primary/30 text-primary" />}>a drop the optimizer adds</Item>
    {hasTags ? <Item mark={<TagDot />}>an hour tag the hour carries</Item> : null}
    {hasTags ? <Item mark={<TagDot unknown />}>a tag that cannot judge the hour — its conditions do nothing</Item> : null}
    {hasRules
      ? (['block', 'no_extra', 'extra_first'] as const).map((a) => {
          const meta = RULE_ACTIONS[a];
          const Icon = meta.icon;
          return (
            <Item key={a} mark={<span className="flex h-3.5 w-4 items-center justify-center rounded" style={{ background: RULE_TONE_COLOR[meta.tone] }}><Icon className="h-2.5 w-2.5 text-white" /></span>}>
              {meta.badge}
            </Item>
          );
        })
      : null}
  </div>
);
